import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import ViewSummaryButton from "../buttons/ViewSummaryButton";

interface ContainerSuccessCardProps {
    containerData: any;
    onViewSummaryClick?: any;
    alertType?: any;
}

interface ContainerSuccessCardState {
    processedRecommendations: any;
    processedDate: any;
    processedBy: any;
}

function ContainerSuccessCard({
    containerData,
    onViewSummaryClick,
    alertType = "Spoilage Alert"
}: ContainerSuccessCardProps) {
    const userEmail = useSelector((state: any) => state.auth?.user?.email) || "monica@kimaru.ai";

    const [processedRecommendations, setProcessedRecommendations] = useState(0);
    const [processedDate, setProcessedDate] = useState<any>(null);
    const [processedBy] = useState("Monica");

    const containerId = containerData.container_id;
    const location = containerData.location;

    // Tick mark SVG icon component
    const TickIcon = () => (
        <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="text-green-500"
        >
            <path
                d="M13.3346 4L6.0013 11.3333L2.66797 8"
                stroke="#111827"
                strokeWidth="1.33"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );

    const fetchProcessedRecommendationsCount = useCallback(async () => {
        try {
            // Fetch accepted recommendations from the correct endpoints
            const [movementAcceptedResponse, priceAcceptedResponse] = await Promise.all([
                fetch(`https://api.stag.kimaru.ai/api/v1/movement_recommendation/get_accepted_recommendations`, {
                    headers: { 'user-email': userEmail },
                }),
                fetch(`https://api.stag.kimaru.ai/api/v1/price_recommendation/get_accepted_recommendations`, {
                    headers: { 'user-email': userEmail },
                })
            ]);

            let movementAcceptedCount = 0;
            let priceAcceptedCount = 0;
            let lastProcessedDate: any = null;

            // Handle movement accepted recommendations
            if (movementAcceptedResponse.ok) {
                const movementAcceptedData: any = await movementAcceptedResponse.json();

                if (movementAcceptedData?.containers?.[containerId]?.accepted_recommendations) {
                    const containerAcceptedRecs = movementAcceptedData.containers[containerId].accepted_recommendations;
                    movementAcceptedCount = containerAcceptedRecs.length;

                    // Find the latest accepted date
                    const dates: any = containerAcceptedRecs
                        .map((rec: any) => rec.accepted_date ? new Date(rec.accepted_date) : null)
                        .filter((date: any) => date !== null);
                    if (dates.length > 0) {
                        lastProcessedDate = new Date(Math.max(...dates));
                    }
                } else if (Array.isArray(movementAcceptedData)) {
                    const containerAcceptedRecs = movementAcceptedData.filter((rec: any) => rec.container_id === containerId);
                    movementAcceptedCount = containerAcceptedRecs.length;

                    const dates: any = containerAcceptedRecs
                        .map((rec: any) => rec.accepted_date ? new Date(rec.accepted_date) : null)
                        .filter((date: any) => date !== null);
                    if (dates.length > 0) {
                        lastProcessedDate = new Date(Math.max(...dates));
                    }
                } else if (movementAcceptedData?.data) {
                    const dataArray = Array.isArray(movementAcceptedData.data) ? movementAcceptedData.data : [];
                    const containerAcceptedRecs = dataArray.filter((rec: any) => rec.container_id === containerId);
                    movementAcceptedCount = containerAcceptedRecs.length;

                    const dates: any = containerAcceptedRecs
                        .map((rec: any) => rec.accepted_date ? new Date(rec.accepted_date) : null)
                        .filter((date: any) => date !== null);
                    if (dates.length > 0) {
                        lastProcessedDate = new Date(Math.max(...dates));
                    }
                }
            }

            // Handle price accepted recommendations
            if (priceAcceptedResponse.ok) {
                const priceAcceptedData: any = await priceAcceptedResponse.json();

                if (priceAcceptedData?.containers?.[containerId]?.accepted_recommendations) {
                    const containerAcceptedRecs = priceAcceptedData.containers[containerId].accepted_recommendations;
                    priceAcceptedCount = containerAcceptedRecs.length;

                    const dates: any = containerAcceptedRecs
                        .map((rec: any) => rec.accepted_date ? new Date(rec.accepted_date) : null)
                        .filter((date: any) => date !== null);
                    if (dates.length > 0) {
                        const priceLatestDate = new Date(Math.max(...dates));
                        if (!lastProcessedDate || priceLatestDate > lastProcessedDate) {
                            lastProcessedDate = priceLatestDate;
                        }
                    }
                } else if (Array.isArray(priceAcceptedData)) {
                    const containerAcceptedRecs = priceAcceptedData.filter((rec: any) => rec.container_id === containerId);
                    priceAcceptedCount = containerAcceptedRecs.length;

                    const dates: any = containerAcceptedRecs
                        .map((rec: any) => rec.accepted_date ? new Date(rec.accepted_date) : null)
                        .filter((date: any) => date !== null);
                    if (dates.length > 0) {
                        const priceLatestDate = new Date(Math.max(...dates));
                        if (!lastProcessedDate || priceLatestDate > lastProcessedDate) {
                            lastProcessedDate = priceLatestDate;
                        }
                    }
                } else if (priceAcceptedData?.data) {
                    const dataArray = Array.isArray(priceAcceptedData.data) ? priceAcceptedData.data : [];
                    const containerAcceptedRecs = dataArray.filter((rec: any) => rec.container_id === containerId);
                    priceAcceptedCount = containerAcceptedRecs.length;

                    const dates: any = containerAcceptedRecs
                        .map((rec: any) => rec.accepted_date ? new Date(rec.accepted_date) : null)
                        .filter((date: any) => date !== null);
                    if (dates.length > 0) {
                        const priceLatestDate = new Date(Math.max(...dates));
                        if (!lastProcessedDate || priceLatestDate > lastProcessedDate) {
                            lastProcessedDate = priceLatestDate;
                        }
                    }
                }
            }

            const totalAcceptedCount = movementAcceptedCount + priceAcceptedCount;
            setProcessedRecommendations(totalAcceptedCount);
            setProcessedDate(lastProcessedDate || new Date());

        } catch (error: any) {
            // Fallback: if APIs fail, show the original recommendation count as processed
            const fallbackCount = containerData?.recommendations?.length || 0;
            setProcessedRecommendations(fallbackCount);
            setProcessedDate(new Date());
        }
    }, [containerId, containerData, userEmail]); // Added userEmail to dependencies

    useEffect(() => {
        if (containerId) {
            // Initialize with fallback values immediately
            setProcessedRecommendations(containerData?.recommendations?.length || 0);
            setProcessedDate(new Date());

            // Then fetch updated values from API
            fetchProcessedRecommendationsCount();
        }
    }, [containerId, containerData, fetchProcessedRecommendationsCount]);

    // Format date for display
    const formatDate = (date: any): string => {
        if (!date) return '';

        const options: any = {
            weekday: 'short',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        };

        return date.toLocaleDateString('en-US', options);
    };

    const handleViewSummaryClick = (): void => {
        // will add this button logic later
    };

    return (
        <div
            className="bg-white shadow-sm"
            style={{
                borderRadius: '6px',
                border: '1px solid #E2E8F0'
            }}
        >
            {/* Header */}
            <div
                className="flex justify-between items-center h-[50px] px-4 py-2 border-b"
                style={{
                    borderRadius: '6px 6px 0 0',
                    border: '1px solid #E2E8F0',
                    background: '#F8FAFC'
                }}
            >
                <div className="flex-1">
                    <h2 className="text-xl font-semibold text-gray-900">
                        {alertType} → {location} #{containerId}
                    </h2>
                </div>
                <div className="text-sm text-gray-500">
                    Asia Pacific {">"} Japan {">"} {location}
                </div>
            </div>

            <div className="flex items-center px-4 py-6">
                {/* Left Section - Success Message */}
                <div className="flex items-center">
                    <div className="flex-shrink-0">
                        <TickIcon />
                    </div>

                    <div className="ml-4">
                        <div style={{ fontWeight: '600' }}>
                            {processedRecommendations} recommendation{processedRecommendations !== 1 ? 's' : ''} processed on
                        </div>
                        <div style={{ fontWeight: '600' }}>
                            {formatDate(processedDate)} by {processedBy}
                        </div>
                    </div>
                </div>

                {/* View Summary Button - using the new component */}
                <div className="ml-6">
                    <ViewSummaryButton onClick={handleViewSummaryClick} />
                </div>

                {/* Empty flex space for layout */}
                <div className="flex-1"></div>
            </div>
        </div>
    );
}
export default ContainerSuccessCard;
